import { useState } from "react";
import { FolderPlus, Folder, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const chip = (on) => `shrink-0 flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 border transition-colors ${on ? "border-cyan-500 bg-cyan-500/15 text-cyan-200" : "border-slate-700 text-slate-400 hover:border-slate-500"}`;

function FolderEditor({ initial, onSave, onCancel, t }) {
  const [name, setName] = useState(initial?.name || "");
  const [color, setColor] = useState(initial?.color || "#F59E0B");
  return (
    <div className="flex items-center gap-1.5 w-full" data-testid="folder-editor">
      <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="folder-color-input"
        className="w-7 h-7 rounded bg-transparent border border-slate-600 cursor-pointer shrink-0" />
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Nazwa katalogu, np. SM Alfa potencjał")}
        onKeyDown={(e) => { if (e.key === "Enter") onSave({ name, color }); }} data-testid="folder-name-input"
        className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-cyan-500" />
      <button onClick={() => onSave({ name, color })} data-testid="folder-save-btn" className="p-1.5 rounded bg-cyan-600 hover:bg-cyan-500"><Check size={13} /></button>
      <button onClick={onCancel} data-testid="folder-cancel-btn" className="p-1.5 rounded hover:bg-slate-700 text-slate-400"><X size={13} /></button>
    </div>
  );
}

// Pasek własnych katalogów modułu: filtr listy + tworzenie / zmiana nazwy / usuwanie.
export function FolderBar({ moduleKey, folders, items, active, setActive, onChanged, canEdit, canDelete, t }) {
  const [edit, setEdit] = useState(null);
  const count = (fid) => items.filter((it) => (fid === "none" ? !it.folder_id : it.folder_id === fid)).length;
  const cur = folders.find((f) => f.id === active);

  const save = async (vals) => {
    try {
      if (edit === "new") { const { data } = await api.post("/folders", { module: moduleKey, ...vals }); setActive(data.id); }
      else await api.put(`/folders/${edit}`, vals);
      setEdit(null); onChanged();
      toast.success(t("Zapisano katalog"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!cur || !window.confirm(`${t("Usunąć katalog")} „${cur.name}”? ${t("Obiekty zostaną, trafią do „Bez katalogu”.")}`)) return;
    try { await api.delete(`/folders/${cur.id}`); setActive("all"); onChanged(); toast.success(t("Usunięto katalog")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (edit) {
    return <div className="px-4 py-2 border-b border-slate-800"><FolderEditor t={t} initial={edit === "new" ? null : cur} onSave={save} onCancel={() => setEdit(null)} /></div>;
  }
  return (
    <div className="px-4 py-2 border-b border-slate-800 flex items-center gap-1.5 overflow-x-auto" data-testid="folder-bar">
      <button onClick={() => setActive("all")} className={chip(active === "all")} data-testid="folder-chip-all">{t("Wszystkie")} <span className="font-mono">{items.length}</span></button>
      {folders.length > 0 && (
        <button onClick={() => setActive("none")} className={chip(active === "none")} data-testid="folder-chip-none">{t("Bez katalogu")} <span className="font-mono">{count("none")}</span></button>
      )}
      {folders.map((f) => (
        <button key={f.id} onClick={() => setActive(f.id)} className={chip(active === f.id)} data-testid={`folder-chip-${f.id}`}>
          <Folder size={11} style={{ color: f.color || "#94a3b8" }} /> {f.name} <span className="font-mono">{count(f.id)}</span>
        </button>
      ))}
      {canEdit && (
        <button onClick={() => setEdit("new")} title={t("Nowy katalog")} data-testid="folder-add-btn"
          className="shrink-0 p-1.5 rounded hover:bg-slate-700 text-cyan-400"><FolderPlus size={14} /></button>
      )}
      {canEdit && cur && (
        <button onClick={() => setEdit(cur.id)} title={t("Zmień nazwę / kolor")} data-testid="folder-edit-btn"
          className="shrink-0 p-1.5 rounded hover:bg-slate-700 text-slate-300"><Pencil size={13} /></button>
      )}
      {canDelete && cur && (
        <button onClick={remove} title={t("Usuń katalog")} data-testid="folder-delete-btn"
          className="shrink-0 p-1.5 rounded hover:bg-red-600/30 text-red-400"><Trash2 size={13} /></button>
      )}
    </div>
  );
}

// Wybór katalogu dla pojedynczego obiektu (na liście).
export function FolderSelect({ moduleKey, item, folders, onChanged, t }) {
  if (!folders.length) return null;
  const move = async (fid) => {
    try { await api.post("/folders/assign", { module: moduleKey, ids: [item.id], folder_id: fid || null }); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <select value={item.folder_id || ""} onChange={(e) => move(e.target.value)} title={t("Katalog")}
      data-testid={`folder-select-${item.id}`} onClick={(e) => e.stopPropagation()}
      className="bg-slate-800 border border-slate-700 rounded px-1 py-1 text-[10px] text-slate-300 outline-none max-w-[110px]">
      <option value="">{t("Bez katalogu")}</option>
      {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
    </select>
  );
}
